import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchableSelect from '../components/SearchableSelect';
import FilterSelect, { useFilterCache } from '../components/FilterSelect';
import { getCsrfToken } from '../utils/auth';
import '../styles/PanelFromText.css';

const EXAMPLE = `default filter 863
complaint -> capa
complaint to non conformity filter 863
capa to eco filter 863
complaint - risk filter 863
risk to software requirement
software req to test case
test case to test run 856`;

const MAX_DEPTH = 6;

// The resolver was NOT sure. The user must say so explicitly before Approve unlocks —
// silence is not consent when the machine was guessing.
// 'inferred' = the level named no project and doesn't exist in the panel's own, so it was found
// in another project mentioned in the description. That's a real decision, not a detail.
const NEEDS_CONFIRM = ['guessed', 'ambiguous', 'ai', 'inferred', 'unresolved'];

export default function PanelFromText() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [baseProject, setBaseProject] = useState(null);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(true);

  const [draft, setDraft] = useState(null);   // the immutable server response (diagnostics, item_types)
  const [tree, setTree] = useState([]);       // the EDITABLE block list — structure + fields
  const [confirmed, setConfirmed] = useState({});
  const [reading, setReading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const revalidateTimer = useRef(null);
  const { filterCache, ensureFilters } = useFilterCache();
  const [orcanosUrl, setOrcanosUrl] = useState('');

  useEffect(() => {
    fetch('/api/config/projects', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setProjects(d.projects || []);
        setOrcanosUrl(d.orcanos_url || '');
      })
      .catch(() => setError('Could not load projects.'));
  }, []);

  const projectLabel = p => `${p.project_name} (${p.version_label})`;
  const projectOptions = useMemo(() => projects.map(projectLabel), [projects]);

  const projectOf = useCallback(
    b => projects.find(p => String(p.project_id) === String(b.project_id)
                         && String(p.version_id) === String(b.version_id)) || baseProject,
    [projects, baseProject],
  );

  // ---------------------------------------------------------------- read

  const readText = useCallback(async () => {
    if (!baseProject || !text.trim()) return;
    setReading(true);
    setError(null);
    setDraft(null);
    setTree([]);
    setConfirmed({});
    try {
      const res = await fetch('/api/config/from-text', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({
          text,
          project_id: baseProject.project_id,
          version_id: baseProject.version_id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not read the description.');
      setDraft(data);
      setTree(data.blocks || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setReading(false);
    }
  }, [baseProject, text]);

  // ------------------------------------------------------- tree structure

  // Depth is DERIVED from parent_id, never stored — so re-parenting a row automatically
  // re-levels its whole subtree. Sibling order is the order rows sit in `tree`.
  const ordered = useMemo(() => {
    const root = tree.find(b => b.is_base);
    if (!root) return [];
    const out = [];
    const walk = (b, depth) => {
      out.push({ ...b, depth });
      tree.filter(c => c.parent_id === b.id).forEach(c => walk(c, depth + 1));
    };
    walk(root, 1);
    return out;
  }, [tree]);

  const depthOf = useCallback(id => (ordered.find(b => b.id === id) || {}).depth || 1, [ordered]);

  // How many levels does this row carry below it? Used to stop an indent that would push a
  // descendant past L6 — the panel only supports six.
  const subtreeDepth = useCallback((id) => {
    const kids = tree.filter(c => c.parent_id === id);
    return kids.length === 0 ? 1 : 1 + Math.max(...kids.map(k => subtreeDepth(k.id)));
  }, [tree]);

  const prevSibling = useCallback((b) => {
    const sibs = tree.filter(x => x.parent_id === b.parent_id);
    const i = sibs.findIndex(x => x.id === b.id);
    return i > 0 ? sibs[i - 1] : null;
  }, [tree]);

  const nextSibling = useCallback((b) => {
    const sibs = tree.filter(x => x.parent_id === b.parent_id);
    const i = sibs.findIndex(x => x.id === b.id);
    return i >= 0 && i < sibs.length - 1 ? sibs[i + 1] : null;
  }, [tree]);

  const canIndent = useCallback((b) => {
    if (b.is_base) return false;
    const p = prevSibling(b);
    if (!p) return false;                                   // nothing above it to become a child of
    return depthOf(b.id) + subtreeDepth(b.id) <= MAX_DEPTH; // would its deepest child still fit?
  }, [prevSibling, depthOf, subtreeDepth]);

  const canOutdent = useCallback((b) => {
    if (b.is_base) return false;
    const parent = tree.find(x => x.id === b.parent_id);
    // Outdenting a child of the root would give the panel a SECOND root, which a trace tree
    // can't express. That's the one move we refuse.
    return !!parent && !parent.is_base;
  }, [tree]);

  const indent = (b) => {
    const p = prevSibling(b);
    if (!p) return;
    setTree(t => t.map(x => (x.id === b.id ? { ...x, parent_id: p.id } : x)));
  };

  const outdent = (b) => {
    const parent = tree.find(x => x.id === b.parent_id);
    if (!parent || parent.is_base) return;
    setTree(t => t.map(x => (x.id === b.id ? { ...x, parent_id: parent.parent_id } : x)));
  };

  // Reorder among siblings: swap the two rows' positions in `tree`. Children ride along, since
  // they're found by parent_id rather than by position.
  const swapWith = (b, other) => {
    if (!other) return;
    setTree(t => {
      const next = [...t];
      const i = next.findIndex(x => x.id === b.id);
      const j = next.findIndex(x => x.id === other.id);
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  // ---------------------------------------------------------- field edits

  // Any edit to type / filter / project invalidates the count the server computed. Leaving the
  // old ✓ on screen would be a badge that lies, so the row goes stale and gets re-checked.
  const patch = (id, fields, staleValidation = true) =>
    setTree(t => t.map(b => (b.id === id
      ? { ...b, ...fields, ...(staleValidation ? { validation: { status: 'stale' } } : {}) }
      : b)));

  useEffect(() => {
    const stale = tree.filter(b => b.validation && b.validation.status === 'stale');
    if (stale.length === 0) return;

    clearTimeout(revalidateTimer.current);
    revalidateTimer.current = setTimeout(async () => {
      try {
        const res = await fetch('/api/config/validate-blocks', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
          body: JSON.stringify({
            blocks: stale.map(b => ({
              id: b.id,
              work_item_type: b.work_item_type,
              filter_id: b.filter_id,
              version_id: b.version_id,
              level_name: b.level_name,
            })),
          }),
        });
        if (!res.ok) return;
        const { results } = await res.json();
        setTree(t => t.map(b => (results[b.id] !== undefined
          ? { ...b, validation: results[b.id] }
          : b)));
      } catch {
        /* leave the row stale; the user can still see it hasn't been checked */
      }
    }, 600);

    return () => clearTimeout(revalidateTimer.current);
  }, [tree]);

  useEffect(() => () => clearTimeout(revalidateTimer.current), []);

  // ---------------------------------------------------------------- gates

  const diagsFor = useCallback(
    id => (draft?.diagnostics || []).filter(d => d.block_id === id && d.status !== 'defaulted'),
    [draft],
  );

  const rowOpen = useCallback((b) => {
    if (!b.work_item_type || !b.filter_id) return true;   // a level with no type or filter can't build
    const unsure = diagsFor(b.id).some(d => NEEDS_CONFIRM.includes(d.status));
    return unsure && !confirmed[b.id];
  }, [diagsFor, confirmed]);

  const openRows = ordered.filter(rowOpen);
  const hardErrors = draft?.errors || [];
  const unparsed = draft?.unparsed || [];
  const canApprove = !!draft && hardErrors.length === 0 && unparsed.length === 0
    && ordered.length > 0 && openRows.length === 0 && name.trim().length > 0;

  const approve = async () => {
    if (!canApprove) return;
    setCreating(true);
    setError(null);
    try {
      const base = ordered.find(b => b.is_base);
      const children = ordered.filter(b => !b.is_base);

      // The exact payload the wizard posts — same endpoint, no second write path.
      const payload = {
        project_id: base.project_id,
        version_id: base.version_id,
        name: name.trim(),
        base_work_item: base.work_item_type,
        base_level_name: base.level_name,
        base_filter_id: parseInt(base.filter_id, 10),
        levels: children.map(b => ({
          level_num: b.depth,
          work_item_type: b.work_item_type,
          work_item_type_label: b.work_item_type_label,
          filter_id: parseInt(b.filter_id, 10),
          level_name: b.level_name,
          project_id: b.project_id,
          version_id: b.version_id,
          must_have: b.must_have !== false,
          id: b.id,
          parent_id: b.parent_id,
        })),
        is_public: isPublic,
      };

      const res = await fetch('/api/config', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not create the panel.');
      navigate(`/trace/matrix/${data.id}`);
    } catch (e) {
      setError(e.message);
      setCreating(false);
    }
  };

  // ----------------------------------------------------------------- view

  const Validation = ({ b }) => {
    const v = b.validation;
    if (!b.filter_id || !b.work_item_type) return <span className="pft-vmsg">—</span>;
    if (!v) return <span className="pft-vmsg">—</span>;
    if (v.status === 'stale') return <span className="pft-vmsg pft-stale">re-checking…</span>;
    return (
      <>
        <span className={`pft-pill pft-${v.status}`}>
          {{ ok: '✓', empty: '⚠ empty', error: '⚠ error' }[v.status]}
        </span>
        <div className="pft-vmsg">{v.message}</div>
      </>
    );
  };

  return (
    <div className="pft">
      <div className="pft-head">
        <button className="pft-back" onClick={() => navigate('/trace/dashboard')}>← Back to Dashboard</button>
        <h2>Describe a panel</h2>
        <p className="pft-sub">Write one line per trace hop. Everything is reviewed before anything is created.</p>
      </div>

      <div className="pft-form">
        <label className="pft-label">Project &amp; version</label>
        <SearchableSelect
          options={projectOptions}
          value={baseProject ? projectLabel(baseProject) : ''}
          placeholder="Pick the project this panel traces…"
          onChange={e => {
            setBaseProject(projects.find(x => projectLabel(x) === e.target.value) || null);
            setDraft(null);
            setTree([]);
          }}
        />

        <label className="pft-label">Description</label>
        <textarea
          className="pft-textarea"
          rows={9}
          spellCheck={false}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={EXAMPLE}
        />
        <div className="pft-hint">
          <code>default filter 863</code> fills in every level that doesn't name its own.
          Separators: <code>-&gt;</code>, <code>to</code>, <code>-</code>. Put a different project
          in parentheses — <code>test case (Cardiology Software 2.0)</code> — and the levels after
          it are looked for there too. Typos are tolerated. The starting level is worked out for
          you: it's the one nothing traces to. You can restructure the result below.
          <button className="pft-example" onClick={() => setText(EXAMPLE)}>Use the example</button>
        </div>

        <button className="btn-primary pft-read" onClick={readText}
                disabled={!baseProject || !text.trim() || reading}>
          {reading ? 'Reading…' : 'Read the description'}
        </button>
      </div>

      {error && <div className="pft-error">{error}</div>}

      {draft && hardErrors.map((e, i) => <div key={i} className="pft-blocker">✋ {e}</div>)}
      {draft && unparsed.map((u, i) => (
        <div key={i} className="pft-blocker">
          ✋ Line {u.line_no} couldn't be read: <code>{u.raw}</code>
          {draft.ai && !draft.ai.enabled && ' — AI interpretation is off, so rewrite it as “A -> B”.'}
          {draft.ai && draft.ai.error && ` — ${draft.ai.error}`}
        </div>
      ))}

      {draft && hardErrors.length === 0 && ordered.length > 0 && (
        <div className="pft-preview">
          <div className="pft-preview-head">
            <h3>Review &amp; approve</h3>
            {openRows.length > 0 && (
              <span className="pft-openbadge">
                {openRows.length} level{openRows.length > 1 ? 's need' : ' needs'} your attention
              </span>
            )}
          </div>

          <table className="pft-table">
            <thead>
              <tr>
                <th>Move</th><th>Level</th><th>Project</th><th>Work-item type</th>
                <th>Filter</th><th>Must have</th><th>In Orcanos</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map(b => {
                const proj = projectOf(b);
                const types = (proj?.item_types || []);
                const typeOpts = types.map(t => `${t.label} (${t.code})`);
                const open = rowOpen(b);
                const notes = diagsFor(b.id);
                return (
                  <tr key={b.id} className={open ? 'pft-row-open' : ''}>
                    <td className="pft-moves">
                      <button title="Move up (swap with the level above it)"
                              disabled={!prevSibling(b) || b.is_base}
                              onClick={() => swapWith(b, prevSibling(b))}>↑</button>
                      <button title="Move down"
                              disabled={!nextSibling(b) || b.is_base}
                              onClick={() => swapWith(b, nextSibling(b))}>↓</button>
                      <button title="Outdent — make this a sibling of its parent (one level shallower)"
                              disabled={!canOutdent(b)}
                              onClick={() => outdent(b)}>←</button>
                      <button title="Indent — make this a child of the level above it (one level deeper)"
                              disabled={!canIndent(b)}
                              onClick={() => indent(b)}>→</button>
                    </td>

                    <td style={{ paddingLeft: `${(b.depth - 1) * 18 + 12}px` }}>
                      <span className="pft-depth">L{b.depth}</span>
                      {b.is_base && <span className="pft-rootbadge">root</span>}
                      <div className="pft-raw">from “{b.raw_name}”</div>
                    </td>

                    <td className="pft-projcell">
                      <SearchableSelect
                        options={projectOptions}
                        value={proj ? projectLabel(proj) : ''}
                        placeholder="Project…"
                        onChange={e => {
                          const p = projects.find(x => projectLabel(x) === e.target.value);
                          if (!p) return;
                          // The type must exist in the NEW project. If it doesn't, clear it —
                          // the row then blocks Approve until a real type is picked, rather than
                          // carrying a code that project has never heard of.
                          const stillValid = (p.item_types || [])
                            .some(t => t.code === b.work_item_type);
                          patch(b.id, {
                            project_id: p.project_id,
                            version_id: p.version_id,
                            ...(stillValid ? {} : { work_item_type: null, work_item_type_label: null }),
                          });
                        }}
                      />
                    </td>

                    <td className="pft-typecell">
                      <SearchableSelect
                        options={typeOpts}
                        value={b.work_item_type_label
                          ? `${b.work_item_type_label} (${b.work_item_type})` : ''}
                        placeholder="Pick a type…"
                        onChange={e => {
                          const t = types.find(x => `${x.label} (${x.code})` === e.target.value);
                          if (t) patch(b.id, {
                            work_item_type: t.code,
                            work_item_type_label: t.label,
                            level_name: t.label,
                          });
                        }}
                      />
                    </td>

                    <td className="pft-filtercell">
                      <FilterSelect
                        projectId={b.project_id || proj?.project_id}
                        itemTypeCode={b.work_item_type}
                        itemTypeLabel={b.work_item_type_label}
                        versionId={b.version_id || proj?.version_id}
                        orcanosUrl={orcanosUrl}
                        value={b.filter_id}
                        placeholder="filter id"
                        cache={filterCache}
                        ensure={ensureFilters}
                        onChange={v => patch(b.id, {
                          filter_id: v === '' || v == null ? null : parseInt(v, 10),
                        })}
                      />
                    </td>

                    <td className="pft-musthave">
                      {b.is_base ? (
                        <span className="pft-vmsg">—</span>
                      ) : (
                        <input
                          type="checkbox"
                          title="An item that doesn't reach this level shows ⚠ Missing. Untick for an optional branch."
                          checked={b.must_have !== false}
                          onChange={e => patch(b.id, { must_have: e.target.checked }, false)}
                        />
                      )}
                    </td>

                    <td><Validation b={b} /></td>

                    <td>
                      {!b.work_item_type && <div className="pft-note pft-note-unresolved">Pick a work-item type.</div>}
                      {!b.filter_id && <div className="pft-note pft-note-missing">Needs a filter id.</div>}
                      {notes.map((d, i) => (
                        <div key={i} className={`pft-note pft-note-${d.status}`}>{d.message}</div>
                      ))}
                      {notes.some(d => NEEDS_CONFIRM.includes(d.status)) && (
                        <label className="pft-confirm">
                          <input type="checkbox" checked={!!confirmed[b.id]}
                                 onChange={e => setConfirmed(c => ({ ...c, [b.id]: e.target.checked }))} />
                          That's right
                        </label>
                      )}
                      {!open && notes.length === 0 && <span className="pft-ok">✓ resolved</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="pft-approve">
            <input className="pft-name" value={name} placeholder="Name this panel…"
                   onChange={e => setName(e.target.value)} />
            <label className="pft-public">
              <input type="checkbox" checked={isPublic}
                     onChange={e => setIsPublic(e.target.checked)} />
              Visible to everyone on this Orcanos account
            </label>
            <button className="btn-primary" disabled={!canApprove || creating} onClick={approve}>
              {creating ? 'Creating…' : 'Approve & create panel'}
            </button>
          </div>
          {!canApprove && (
            <div className="pft-why">
              {openRows.length > 0
                ? 'Resolve every highlighted level before creating.'
                : !name.trim() ? 'Give the panel a name.' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
